import { cva, type VariantProps } from 'class-variance-authority'
import {
    CircleAlertIcon,
    CircleCheckIcon,
    InfoIcon,
    TriangleAlertIcon,
    XIcon,
} from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

const alertVariants = cva(
    'relative flex w-full items-start gap-3 rounded-lg px-4 py-3 text-sm [&_svg]:size-5 [&_svg]:shrink-0',
    {
        variants: {
            severity: {
                success: '',
                info: '',
                warning: '',
                error: '',
            },
            variant: {
                standard: '',
                filled: 'text-white [&_svg]:text-white',
                outlined: 'border bg-transparent',
            },
        },
        compoundVariants: [
            // standard
            {
                variant: 'standard',
                severity: 'success',
                className:
                    'bg-green-50 text-green-900 [&_svg]:text-green-600 dark:bg-green-950/40 dark:text-green-100',
            },
            {
                variant: 'standard',
                severity: 'info',
                className:
                    'bg-sky-50 text-sky-900 [&_svg]:text-sky-600 dark:bg-sky-950/40 dark:text-sky-100',
            },
            {
                variant: 'standard',
                severity: 'warning',
                className:
                    'bg-amber-50 text-amber-900 [&_svg]:text-amber-600 dark:bg-amber-950/40 dark:text-amber-100',
            },
            {
                variant: 'standard',
                severity: 'error',
                className:
                    'bg-red-50 text-red-900 [&_svg]:text-red-700 dark:bg-red-950/40 dark:text-red-100',
            },
            // filled
            {
                variant: 'filled',
                severity: 'success',
                className: 'bg-green-600',
            },
            { variant: 'filled', severity: 'info', className: 'bg-sky-600' },
            {
                variant: 'filled',
                severity: 'warning',
                className: 'bg-amber-600',
            },
            { variant: 'filled', severity: 'error', className: 'bg-red-600' },
            // outlined
            {
                variant: 'outlined',
                severity: 'success',
                className:
                    'border-green-600 text-green-700 [&_svg]:text-green-600 dark:text-green-300',
            },
            {
                variant: 'outlined',
                severity: 'info',
                className:
                    'border-sky-600 text-sky-700 [&_svg]:text-sky-600 dark:text-sky-300',
            },
            {
                variant: 'outlined',
                severity: 'warning',
                className:
                    'border-amber-600 text-amber-800 [&_svg]:text-amber-600 dark:text-amber-300',
            },
            {
                variant: 'outlined',
                severity: 'error',
                className:
                    'border-red-600 text-red-700 [&_svg]:text-red-600 dark:text-red-300',
            },
        ],
        defaultVariants: {
            severity: 'success',
            variant: 'standard',
        },
    },
)

const defaultIcons = {
    success: CircleCheckIcon,
    info: InfoIcon,
    warning: TriangleAlertIcon,
    error: CircleAlertIcon,
}

type AlertProps = Omit<React.ComponentProps<'div'>, 'title'> &
    VariantProps<typeof alertVariants> & {
        /** Ícone customizado. Passe `false` para ocultar o ícone. */
        icon?: React.ReactNode | false
        /** Conteúdo renderizado à direita (ex.: botão de ação). */
        action?: React.ReactNode
        /** Quando definido, renderiza um botão de fechar. */
        onClose?: () => void
    }

function Alert({
    className,
    severity = 'success',
    variant = 'standard',
    icon,
    action,
    onClose,
    children,
    ...props
}: AlertProps) {
    const DefaultIcon = defaultIcons[severity ?? 'success']

    return (
        <div
            role="alert"
            data-slot="alert"
            data-severity={severity}
            className={cn(alertVariants({ severity, variant }), className)}
            {...props}
        >
            {icon !== false && (
                <span data-slot="alert-icon" className="flex py-0.5">
                    {icon ?? <DefaultIcon />}
                </span>
            )}

            <div
                data-slot="alert-content"
                className="min-w-0 flex-1 self-center py-0.5"
            >
                {children}
            </div>

            {action && (
                <span
                    data-slot="alert-action"
                    className="-mr-1 ml-auto self-start"
                >
                    {action}
                </span>
            )}

            {onClose && (
                <button
                    type="button"
                    aria-label="Fechar"
                    onClick={onClose}
                    className="-mr-1 ml-auto self-start rounded-md p-0.5 opacity-70 transition-opacity hover:opacity-100"
                >
                    <XIcon className="size-4" />
                </button>
            )}
        </div>
    )
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="alert-title"
            className={cn('mb-1 font-semibold leading-5', className)}
            {...props}
        />
    )
}

export { Alert, AlertTitle, alertVariants }
